import { HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { NotificationService } from '../services/notification.service';
import { buildRateLimitMessage } from '../utils/rate-limit.util';
import { extractApiErrorMessage } from '../utils/api-error.util';
import { getModuleLabel } from '../constants/app-modules.constants';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<any>(null);

/** Routes pré-login : pas de Bearer, pas de refresh/logout sur 401. */
function isPublicAuthUrl(url: string): boolean {
  return (
    url.includes('/auth/login') ||
    url.includes('/auth/login/verify-otp') ||
    url.includes('/auth/login/verify-totp') ||
    url.includes('/auth/totp/') ||
    url.includes('/auth/force-change-password') ||
    url.includes('/auth/password-reset/') ||
    url.includes('/auth/account-activation') ||
    url.includes('/auth/sso/') ||
    url.includes('/auth/social/') ||
    url.includes('/auth/refresh')
  );
}

/** Pages d'échange ticket OIDC : ne pas toast/rediriger « Session expirée » pendant le login. */
function isOidcCallbackPage(routerUrl: string): boolean {
  return (
    routerUrl.includes('/auth/sso/callback') ||
    routerUrl.includes('/auth/social/callback') ||
    routerUrl.includes('/social/callback')
  );
}

function shouldAnnounceSessionExpired(routerUrl: string): boolean {
  return !routerUrl.includes('/auth/login') && !isOidcCallbackPage(routerUrl);
}

export function AuthInterceptor(request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<any> {
  const authService = inject(AuthService);
  const notificationService = inject(NotificationService);
  const router = inject(Router);

  const isPublicAuthRoute = isPublicAuthUrl(request.url);

  if (!isPublicAuthRoute) {
    const token = authService.getAccessToken();
    if (token) {
      request = addToken(request, token);
    }
  }

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 429) {
        if (request.url.includes('/auth/refresh')) {
          return throwError(() => error);
        }

        const baseMessage = extractApiErrorMessage(
          error,
          'Trop de requêtes. Veuillez réessayer plus tard.'
        );

        if (!request.url.includes('/auth/login') && !request.url.includes('/auth/social/')) {
          const rateLimitMessage = buildRateLimitMessage(baseMessage, error.headers);
          notificationService.error('Limite atteinte', rateLimitMessage);
        }

        return throwError(() => error);
      }

      if (error.status === 403 && error.error?.mustChangePassword) {
        const utilisateur = error.error?.data?.utilisateur;
        if (utilisateur) {
          authService.setMustChangePasswordRequired(
            utilisateur,
            error.error?.isFirstLogin || false
          );
        } else {
          authService.revokeSessionAfterPasswordChange();
        }

        if (!router.url.includes('/auth/login')) {
          notificationService.warning(
            'Changement de mot de passe requis',
            error.error?.error || 'Veuillez définir un nouveau mot de passe pour continuer.'
          );
          router.navigate(['/auth/login']);
        }

        return throwError(() => error);
      }

      // Lot 1 — module non activé pour le profil
      if (error.status === 403 && Array.isArray(error.error?.requiredModules)) {
        const required = (error.error.requiredModules as string[])
          .map((code) => getModuleLabel(String(code)))
          .join(', ');
        const detail = required ? ` Module(s) requis : ${required}.` : '';
        notificationService.error(
          'Accès refusé',
          `${error.error?.error || 'Module non activé pour ce profil.'}${detail}`
        );
        return throwError(() => error);
      }

      if (
        error.status === 401 &&
        !isPublicAuthRoute &&
        !shouldAttemptTokenRefresh(error)
      ) {
        authService.logoutLocal();
        if (shouldAnnounceSessionExpired(router.url)) {
          notificationService.error(
            'Session expirée',
            getAuthErrorMessage(error)
          );
          router.navigate(['/auth/login']);
        }
        return throwError(() => error);
      }

      if (error.status === 401 && !isPublicAuthRoute) {
        return handle401Error(request, next, authService);
      }

      return throwError(() => error);
    })
  );
}

function shouldAttemptTokenRefresh(error: HttpErrorResponse): boolean {
  const message = (error.error?.error || error.error?.message || '').toLowerCase();
  const noRefreshIndicators = [
    'révoqué',
    'revoke',
    'rôle a changé',
    'role a change',
    'token invalide',
    'rôle manquant'
  ];
  return !noRefreshIndicators.some((indicator) => message.includes(indicator));
}

function getAuthErrorMessage(error: HttpErrorResponse): string {
  return (
    error.error?.error ||
    error.error?.message ||
    'Votre session a expiré. Veuillez vous reconnecter.'
  );
}

function addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`
    }
  });
}

function handle401Error(request: HttpRequest<any>, next: HttpHandlerFn, authService: AuthService): Observable<any> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    return authService.refreshToken().pipe(
      switchMap((response: any) => {
        isRefreshing = false;
        refreshTokenSubject.next(response.data.tokens.accessToken);
        return next(addToken(request, response.data.tokens.accessToken));
      }),
      catchError((error) => {
        isRefreshing = false;
        refreshTokenSubject.next(null);
        authService.logoutLocal();

        const notificationService = inject(NotificationService);
        const router = inject(Router);

        const errorMessage = getAuthErrorMessage(error);

        if (shouldAnnounceSessionExpired(router.url)) {
          notificationService.error('Session expirée', errorMessage);
          router.navigate(['/auth/login']);
        }

        return throwError(() => error);
      })
    );
  }

  return refreshTokenSubject.pipe(
    filter(token => token !== null),
    take(1),
    switchMap(token => next(addToken(request, token)))
  );
}
